/**
 * 메인 App 컴포넌트
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { useAuthStore } from './store/authStore';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { MainLayout } from './components/layout/MainLayout';

// Pages
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { SystemDashboard } from './pages/dashboard/SystemDashboard';
import { GroupDashboard } from './pages/dashboard/GroupDashboard';
import { UsersPage } from './pages/users/UsersPage';
import { UploadPage } from './pages/Workbooks/Upload';
import { WorkbooksPage } from './pages/Workbooks/WorkbooksPage';
import { WorkbookDetailPage } from './pages/Workbooks/WorkbookDetailPage';
import { StudentGradingPage } from './pages/Grading/StudentGradingPage';
import { PageResultDetailPage } from './pages/Grading/PageResultDetailPage';
import { StatsDashboard } from './pages/stats/StatsDashboard';
import { StudentStatsPage } from './pages/stats/StudentStatsPage';
import { WorkbookStatsPage } from './pages/stats/WorkbookStatsPage';
import { StudentProgressPage } from './pages/stats/StudentProgressPage';
import { GroupsPage } from './pages/groups/GroupsPage';
import { GroupDetailPage } from './pages/groups/GroupDetailPage';
import { MyStudentsPage } from './pages/Students/MyStudentsPage';

function App() {
  const { initAuth } = useAuthStore();

  // 앱 시작 시 인증 상태 복원
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <ConfigProvider locale={koKR}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected Routes - System Admin */}
          <Route
            path="/system"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <MainLayout>
                  <SystemDashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Group Admin & Instructor */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['group_admin', 'instructor']}>
                <MainLayout>
                  <GroupDashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Group Management (Admin & Group Admin) */}
          <Route
            path="/groups"
            element={
              <ProtectedRoute allowedRoles={['admin', 'group_admin']}>
                <MainLayout>
                  <GroupsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/:groupId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'group_admin']}>
                <MainLayout>
                  <GroupDetailPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - User Management (Admin & Group Admin) */}
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'group_admin']}>
                <MainLayout>
                  <UsersPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Workbook Upload (Admin Only) */}
          <Route
            path="/workbooks/upload"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <MainLayout>
                  <UploadPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Workbooks Management */}
          <Route
            path="/workbooks"
            element={
              <ProtectedRoute allowedRoles={['admin', 'group_admin', 'instructor']}>
                <MainLayout>
                  <WorkbooksPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workbooks/:workbookName"
            element={
              <ProtectedRoute allowedRoles={['admin', 'group_admin', 'instructor']}>
                <MainLayout>
                  <WorkbookDetailPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - My Students (Instructor Only) */}
          <Route
            path="/students"
            element={
              <ProtectedRoute allowedRoles={['instructor']}>
                <MainLayout>
                  <MyStudentsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Grading Management (Instructor & Group Admin) */}
          <Route
            path="/grading"
            element={
              <ProtectedRoute allowedRoles={['group_admin', 'instructor']}>
                <MainLayout>
                  <StudentGradingPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/grading/:studentId/:workbook/page/:page"
            element={
              <ProtectedRoute allowedRoles={['group_admin', 'instructor']}>
                <MainLayout>
                  <PageResultDetailPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Statistics (Instructor & Group Admin) */}
          <Route
            path="/stats"
            element={
              <ProtectedRoute allowedRoles={['group_admin', 'instructor']}>
                <MainLayout>
                  <StatsDashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stats/students/:studentId"
            element={
              <ProtectedRoute allowedRoles={['group_admin', 'instructor']}>
                <MainLayout>
                  <StudentStatsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stats/workbooks/:workbookName"
            element={
              <ProtectedRoute allowedRoles={['group_admin', 'instructor']}>
                <MainLayout>
                  <WorkbookStatsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stats/students/:studentId/workbooks/:workbookName/progress"
            element={
              <ProtectedRoute allowedRoles={['group_admin', 'instructor']}>
                <MainLayout>
                  <StudentProgressPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/* 404 Not Found */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
